import clsx from 'clsx';
import { Field, Form, Formik } from 'formik';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';

import { OmniFormValues, CredentialType, credentialTitles } from '../types';

import { validationSchema } from './OmniCredentialsForm.validation';
import { CredentialsFormActions } from './CredentialsFormActions';
import { StyledTextArea } from './SSHCredentialsForm/SSHCredentialsPrivateKeyForm';

const defaultInitialValues: OmniFormValues = {
  name: '',
  credentials: {
    serviceAccountKey: '',
    endpoint: '',
  },
};

type Props = {
  selectedProvider: CredentialType;
  isEditing?: boolean;
  isLoading: boolean;
  onSubmit: (formValues: OmniFormValues) => void;
  credentialNames: string[];
  inDrawer?: boolean;
  initialValues?: OmniFormValues;
  placeholderText?: string;
};

export function OmniCredentialsForm({
  selectedProvider,
  isEditing = false,
  isLoading,
  onSubmit,
  credentialNames,
  inDrawer,
  initialValues = defaultInitialValues,
  placeholderText = `e.g. eyIAYW1lIjoiYWxGxNMEtvUEdQIFBSSVZBVEUgS0VZIEJMT0NLLS0tLS0ifQ==`,
}: Props) {
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema(credentialNames, isEditing)}
      onSubmit={(values) => onSubmit(values)}
      validateOnMount
    >
      {({ values, errors, handleSubmit, isValid, dirty }) => (
        <Form
          className={clsx('form-horizontal', inDrawer && 'flex grow flex-col')}
          onSubmit={handleSubmit}
          noValidate
        >
          <FormControl
            inputId="name"
            label="Credentials name"
            size="medium"
            errors={errors.name}
          >
            <Field
              as={Input}
              name="name"
              id="name"
              value={values.name}
              placeholder={`e.g. ${credentialTitles[selectedProvider]} Staging`}
              data-cy="cloudSettings-apiKeyName"
            />
          </FormControl>
          <FormControl
            inputId="endpoint"
            label="Endpoint URL"
            size="medium"
            tooltip="The 'OMNI_ENDPOINT' URL shown after using omnictl to create a service account"
            errors={errors.credentials?.endpoint}
          >
            <Field
              as={Input}
              // see https://formik.org/docs/guides/arrays#nested-objects for the longer Name
              name="credentials.endpoint"
              autoComplete="off"
              id="endpoint"
              value={values.credentials.endpoint}
              placeholder="https://api.omni.com:443"
              data-cy="cloudSettings-endpoint"
            />
          </FormControl>

          <FormControl
            inputId="service_account_key"
            label="Service account key"
            size="medium"
            tooltip="The 'OMNI_SERVICE_ACCOUNT_KEY' shown after using omnictl to create a service account"
            errors={errors.credentials?.serviceAccountKey}
          >
            <Field
              as={StyledTextArea}
              name="credentials.serviceAccountKey"
              autoComplete="off"
              type="password"
              id="service_account_token"
              value={values.credentials.serviceAccountKey}
              placeholder={placeholderText}
              data-cy="cloudSettings-serviceAccountKey"
            />
          </FormControl>

          <CredentialsFormActions
            isEditing={isEditing}
            isLoading={isLoading}
            disabled={!isValid || !dirty}
            inDrawer={inDrawer}
          />
        </Form>
      )}
    </Formik>
  );
}
